import clsx from 'clsx';
import {
  type ElementType,
  memo,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { TextVariant } from '../../types/TextVariant';

import { Skeleton } from '../Skeleton/Skeleton';
import { Tooltip } from '../Tooltip/Tooltip';
import { TextTone } from './types';
import {
  resolveFont,
  resolveTooltipText,
  resolveTruncateLines,
  truncateCenterToWidth,
} from './utils';
import './Text.css';

export type TruncationMode = 'center' | 'end' | 'start';

interface Props {
  /** The DOM element to render (default is `span`) */
  as?: ElementType;
  /** React node */
  children?: ReactNode;
  /** The ID of the text element, used for aria-labelledby */
  id?: string;
  /** Replaces the content with a skeleton bar matching the `variant` line height. @default false */
  isLoading?: boolean;
  /** Foreground tone */
  tone?: TextTone;
  /** Truncate after this many lines. `true` truncates after 1 line. */
  truncate?: boolean | number;
  /** Where to omit text when truncating a single-line string. */
  truncationMode?: TruncationMode;
  /** Text variant */
  variant?: TextVariant;
  __private?: {
    /**
     * Set when an interactive host (button, link) already owns focus — the
     * overflow tooltip stays hover-only instead of adding a nested tab stop.
     */
    inInteractive?: boolean;
  };
}

export const Text = memo(function Text(props: Props) {
  const {
    as: Component = 'span',
    children,
    id,
    isLoading = false,
    tone = 'auto',
    truncate,
    truncationMode = 'end',
    variant = 'body-m',
    __private,
  } = props;

  const [centerText, setCenterText] = useState<string | undefined>();
  const [tooltip, setTooltip] = useState<string | undefined>();
  const ref = useRef<HTMLElement>(null);
  const truncateLines = resolveTruncateLines(truncate);
  const resolvedTruncationMode =
    truncateLines === 1 && typeof children === 'string'
      ? truncationMode
      : 'end';

  useLayoutEffect(() => {
    const element = ref.current;
    if (!truncateLines || !element) {
      setCenterText(undefined);
      setTooltip(undefined);
      return;
    }

    const update = () => {
      let overflown: boolean;
      if (resolvedTruncationMode === 'center' && typeof children === 'string') {
        const next = truncateCenterToWidth(
          children,
          element.clientWidth,
          resolveFont(element),
        );
        setCenterText((previous) => (previous === next ? previous : next));
        overflown = next !== children;
      } else {
        overflown =
          truncateLines === 1
            ? element.scrollWidth > element.clientWidth
            : element.scrollHeight > element.clientHeight;
      }
      const nextTooltip = resolveTooltipText(overflown, children, element);
      setTooltip((previous) =>
        previous === nextTooltip ? previous : nextTooltip,
      );
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [children, resolvedTruncationMode, truncateLines]);

  let content = children;
  if (resolvedTruncationMode === 'center' && typeof children === 'string') {
    content = (
      <>
        <span className="eds-text--truncate-accessible">{children}</span>
        {/*
         * The full string stays in flow so the element keeps the width it would
         * have with untruncated text. Measuring the visible copy instead would
         * feed each truncation back into the next measurement.
         */}
        <span aria-hidden className="eds-text--truncate-center-measure">
          {children}
        </span>
        <bdi aria-hidden className="eds-text--truncate-center-visible">
          {centerText ?? children}
        </bdi>
      </>
    );
  } else if (
    resolvedTruncationMode === 'start' &&
    typeof children === 'string'
  ) {
    content = <bdi>{children}</bdi>;
  }

  if (isLoading) {
    return (
      <div
        className="eds-text--loading"
        style={{ height: `var(--eds--typography--${variant}-line-height)` }}
      >
        <Skeleton size="fill" />
      </div>
    );
  }

  return (
    <Tooltip tooltip={tooltip} __private={{ wrapTrigger: !!truncateLines }}>
      <Component
        className={clsx(
          'eds-text',
          `eds-text--tone-${tone}`,
          `eds-text--variant-${variant}`,
          truncateLines && 'eds-text--truncate',
          truncateLines && `eds-text--truncate-${resolvedTruncationMode}`,
          truncateLines === 1 && 'eds-text--truncate-single',
          truncateLines && truncateLines > 1 && 'eds-text--truncate-multiline',
        )}
        id={id}
        ref={ref}
        style={
          truncateLines
            ? { '--eds-text--line-clamp': truncateLines }
            : undefined
        }
        tabIndex={tooltip && !__private?.inInteractive ? 0 : undefined}
      >
        {content}
      </Component>
    </Tooltip>
  );
});

Text.displayName = 'Text';
